import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Pencil, Trash2, X, Check, User, Phone, Mail, Calendar, Link as LinkIcon, Search, ArrowDown, ArrowUp, Filter, Handshake, ChevronDown } from 'lucide-react';
import { Collab, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/supabaseHelpers';
import { Dialog, Listbox } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContextSupabase';

interface Contact {
  name: string;
  role: string;
  email: string;
  phone: string;
}

interface Client {
  id?: string;
  agency_id: string;
  brand_name: string;
  sector: string;
  contacts: Contact[];
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

const BUSINESS_SECTORS = [
  'Aéronautique & Spatial',
  'Agriculture & Agro-alimentaire',
  'Ameublement & Décoration',
  'Architecture & BTP',
  'Art & Culture',
  'Assurance',
  'Automobile',
  'Banque & Finance',
  'Beauté & Cosmétiques',
  'Bijouterie & Joaillerie',
  'Brasserie & Boissons alcoolisées',
  'Cafés & Restaurants',
  'Chimie & Pharmacie',
  'Commerce & Distribution',
  'Communication & Marketing',
  'Conseil & Services aux entreprises',
  'Cybersécurité',
  'Design',
  'E-commerce',
  'Éducation & Formation',
  'Électronique',
  'Énergie & Environnement',
  'Événementiel',
  'Film & Production audiovisuelle',
  'Fitness & Bien-être',
  'Food & Beverage',
  'Gaming & Esport',
  'Hôtellerie',
  'Immobilier',
  'Industrie',
  'Informatique & Software',
  'Jeux & Jouets',
  'Juridique',
  'Lifestyle',
  'Logistique & Transport',
  'Luxe',
  'Média & Presse',
  'Mode & Textile',
  'Musique',
  'Nautisme',
  'Petfood & Animaux',
  'Retail',
  'Santé & Médical',
  'Services à la personne',
  'Sport & Équipements sportifs',
  'Technologie & High-tech',
  'Télécommunications',
  'Tourisme & Voyage',
  'Autre'
];

export default function Clients() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [clientCollabCounts, setClientCollabCounts] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [sortBy, setSortBy] = useState<'collaborations' | 'date' | 'name'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState<Partial<Client>>({
    brand_name: '',
    contacts: [],
    sector: '',
    notes: ''
  });

  useEffect(() => {
    if (currentUser && userProfile?.agency_id) {
      loadClients();
    }
  }, [userProfile?.agency_id]);

  useEffect(() => {
    if (clients.length > 0 && collabs.length > 0) {
      const counts: Record<string, number> = {};

      clients.forEach(client => {
        counts[client.id!] = collabs.filter(collab =>
          collab.brand === client.brand_name
        ).length;
      });

      setClientCollabCounts(counts);
    }
  }, [clients, collabs]);

  const loadClients = async () => {
    if (!currentUser || !userProfile?.agency_id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [clientsData, collabsData] = await Promise.all([
        getDocuments<Client>('clients', userProfile.agency_id),
        getDocuments<Collab>('collabs', userProfile.agency_id)
      ]);

      console.log('Loaded clients:', clientsData);
      setClients(clientsData);
      setCollabs(collabsData);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSort = (field: 'collaborations' | 'date' | 'name') => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setFormData({
      brand_name: '',
      contacts: [],
      sector: '',
      notes: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile?.agency_id) return;

    try {
      if (editingClient?.id) {
        await updateDocument('clients', editingClient.id, formData);
      } else {
        await createDocument('clients', {
          ...formData,
          agency_id: userProfile.agency_id,
          brand_name: formData.brand_name!,
          contacts: formData.contacts || [],
          sector: formData.sector || '',
          notes: formData.notes || ''
        } as Client);
      }
      await loadClients();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (!clientToDelete?.id) return;
      await deleteDocument('clients', clientToDelete.id);
      await loadClients();
      setIsDeleteModalOpen(false);
      setClientToDelete(null);
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      brand_name: client.brand_name,
      contacts: [...client.contacts],
      sector: client.sector,
      notes: client.notes
    });
    setIsModalOpen(true);
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), {
        name: '',
        email: '',
        phone: '',
        type: 'brand',
        role: ''
      }]
    }));
  };

  const handleRemoveContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.filter((_, i) => i !== index) || []
    }));
  };

  const handleContactChange = (index: number, field: keyof Contact, value: string) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      ) || []
    }));
  };

  const handleViewDetails = (client: Client) => {
    navigate(`/app/clients/${client.id}`);
  };

  const filteredClients = clients.filter(client => {
    const searchLower = search.toLowerCase();
    return (
      client.brand_name.toLowerCase().includes(searchLower) ||
      client.contacts.some(contact => 
        contact.name.toLowerCase().includes(searchLower) ||
        contact.email.toLowerCase().includes(searchLower) ||
        (contact.phone && contact.phone.toLowerCase().includes(searchLower))
      ) ||
      (client.notes && client.notes.toLowerCase().includes(searchLower))
    );
  });

  // Sort clients based on current sort criteria
  const sortedClients = [...filteredClients].sort((a, b) => {
    if (sortBy === 'name') {
      return sortDirection === 'asc' 
        ? a.brand_name.localeCompare(b.brand_name)
        : b.brand_name.localeCompare(a.brand_name);
    }
    
    if (sortBy === 'date') {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    // Sort by number of collaborations
    if (sortBy === 'collaborations') {
      const countA = clientCollabCounts[a.id!] || 0;
      const countB = clientCollabCounts[b.id!] || 0;
      return sortDirection === 'asc' ? countA - countB : countB - countA;
    }
    
    return 0; // Default case
  });

  // Get current clients
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = sortedClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(sortedClients.length / clientsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.08] flex items-center justify-center">
            <Building2 className="w-7 h-7 text-white/60" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Clients</h1>
            <p className="text-white/50 mt-0.5">Gérez votre portefeuille clients</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all"
        >
          <Plus className="w-5 h-5" />
          Ajouter un client
        </button>
      </div>

      <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] overflow-hidden">
        <div className="p-5 border-b border-white/[0.08] bg-white/[0.02]">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                placeholder="Rechercher un client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-shadow"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => toggleSort('name')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  sortBy === 'name'
                    ? 'bg-white text-black font-semibold'
                    : 'bg-white/[0.08] text-white/60 border border-white/[0.08] hover:bg-white/[0.14]'
                }`}
              >
                <span>Nom</span>
                {sortBy === 'name' && (
                  sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => toggleSort('date')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  sortBy === 'date'
                    ? 'bg-white text-black font-semibold'
                    : 'bg-white/[0.08] text-white/60 border border-white/[0.08] hover:bg-white/[0.14]'
                }`}
              >
                <span>Date d'ajout</span>
                {sortBy === 'date' && (
                  sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => toggleSort('collaborations')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  sortBy === 'collaborations'
                    ? 'bg-white text-black font-semibold'
                    : 'bg-white/[0.08] text-white/60 border border-white/[0.08] hover:bg-white/[0.14]'
                }`}
                title="Trier par nombre de collaborations"
              >
                <Handshake className="w-4 h-4" />
                <span>Collaborations</span>
                {sortBy === 'collaborations' && (
                  sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/10 border-t-white/60"></div>
          </div>
        ) : !filteredClients || filteredClients.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-2xl bg-white/[0.06] flex items-center justify-center mx-auto mb-5">
              <Building2 className="w-10 h-10 text-white/30" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {search ? 'Aucun résultat trouvé' : 'Aucun client'}
            </h3>
            <p className="text-white/30 max-w-sm mx-auto">
              {search ? 'Essayez de modifier vos critères de recherche' : 'Commencez par ajouter votre premier client'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/[0.04]">
              <thead>
                <tr className="bg-white/[0.02] border-b border-white/[0.06]">
                  <th className="px-6 py-4 text-left text-[10px] font-semibold text-white/40 uppercase tracking-widest">Client</th>
                  <th className="px-6 py-4 text-left text-[10px] font-semibold text-white/40 uppercase tracking-widest">Contacts</th>
                  <th className="px-6 py-4 text-left text-[10px] font-semibold text-white/40 uppercase tracking-widest">Notes</th>
                  <th className="px-6 py-4 text-right text-[10px] font-semibold text-white/40 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {currentClients.map((client) => (
                  <tr key={client.id} className="hover:bg-white/[0.04] transition-colors group">
                    <td
                      className="px-6 py-5 cursor-pointer"
                      onClick={() => navigate(`/app/clients/${client.id}`)}
                    >
                      <div>
                        <div className="text-white font-semibold text-base group-hover:text-white/80 transition-colors">
                          {client.brand_name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.06] rounded-lg">
                            <Handshake className="w-3.5 h-3.5 text-white/40" />
                            <span className="text-xs font-medium text-white/60">
                              {clientCollabCounts[client.id!] || 0} collaboration{clientCollabCounts[client.id!] !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        {client.sector && typeof client.sector === 'string' && client.sector.trim() && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2.5 py-1 bg-white/[0.06] text-white/50 rounded-lg text-xs font-medium">
                              {client.sector}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td
                      className="px-6 py-5 cursor-pointer"
                      onClick={() => navigate(`/app/clients/${client.id}`)}
                    >
                      <div className="space-y-2">
                        {client.contacts.map((contact, index) => (
                          <div key={index} className="flex items-center gap-2.5 text-sm">
                            <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                              {contact.type === 'brand' ? (
                                <Building2 className="w-4 h-4 text-white/40" />
                              ) : (
                                <User className="w-4 h-4 text-white/40" />
                              )}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-white font-medium truncate">{contact.name}</span>
                              <span className="text-white/50 text-xs truncate">{contact.email}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td
                      className="px-6 py-5 text-white/50 cursor-pointer max-w-xs"
                      onClick={() => navigate(`/app/clients/${client.id}`)}
                    >
                      <span className="line-clamp-2 text-sm">{client.notes || '—'}</span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(client)}
                          className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-all"
                          title="Modifier"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            setClientToDelete(client);
                            setIsDeleteModalOpen(true);
                          }}
                          className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                          title="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-5 border-t border-white/[0.06]">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-xl bg-white/[0.08] border border-white/[0.08] text-white/60 font-medium hover:bg-white/[0.14] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Précédent
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(number => {
                    if (number <= 3) return true;
                    if (number === totalPages) return true;
                    if (number === currentPage) return true;
                    if (number === currentPage - 1 || number === currentPage + 1) return true;
                    return false;
                  })
                  .map((number, index, array) => {
                    if (index > 0 && array[index - 1] !== number - 1) {
                      return (
                        <span key={`ellipsis-${number}`} className="text-white/30 px-2 font-semibold">...</span>
                      );
                    }
                    return (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-4 py-2 rounded-xl font-medium transition-all ${
                          currentPage === number
                            ? 'bg-white text-black font-semibold'
                            : 'bg-white/[0.08] border border-white/[0.08] text-white/60 hover:bg-white/[0.14]'
                        }`}
                      >
                        {number}
                      </button>
                    );
                  })}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-xl bg-white/[0.08] border border-white/[0.08] text-white/60 font-medium hover:bg-white/[0.14] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
            <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full shadow-2xl">
              <div className="absolute top-5 right-5 z-10">
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-xl transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="p-6 border-b border-white/[0.08]">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white/60" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">
                      {editingClient ? 'Modifier le client' : 'Ajouter un client'}
                    </h2>
                  </div>
                </div>

                <div className="px-6 pb-6">

                  <div className="space-y-5">
                    <div>
                      <label htmlFor="brand_name" className="block text-sm font-semibold text-white/60 mb-2">
                        Nom du client
                      </label>
                      <input
                        type="text"
                        id="brand_name"
                        value={formData.brand_name}
                        onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                        className="block w-full rounded-xl bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 px-4 py-2.5 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                        placeholder="Nom de la marque ou de l'agence"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="notes" className="block text-sm font-semibold text-white/60 mb-2">
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="block w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl px-4 py-2.5 focus:ring-1 focus:ring-white/20 outline-none transition-all resize-none"
                        placeholder="Ajoutez des notes sur ce client..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white/60 mb-2">
                        Secteur
                      </label>
                      <Listbox value={formData.sector || ''} onChange={(value) => setFormData({ ...formData, sector: value })}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-2.5 text-left text-white focus:ring-1 focus:ring-white/20 outline-none cursor-pointer transition-all">
                            <span className={`block truncate ${!formData.sector ? 'text-white/30' : 'text-white'}`}>
                              {formData.sector || 'Sélectionner un secteur'}
                            </span>
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <ChevronDown className="h-5 w-5 text-white/40" />
                            </span>
                          </Listbox.Button>
                          <Listbox.Options className="absolute z-50 mt-2 w-full bg-[#141414] border border-white/[0.08] rounded-xl shadow-xl max-h-60 overflow-auto focus:outline-none">
                            <Listbox.Option
                              key="empty"
                              value=""
                              className={({ active }) =>
                                `cursor-pointer select-none relative py-2.5 px-4 ${
                                  active ? 'bg-white/[0.08] text-white' : 'text-white/50'
                                }`
                              }
                            >
                              {({ selected }) => (
                                <span className={selected ? 'font-semibold' : 'font-normal'}>
                                  Sélectionner un secteur
                                </span>
                              )}
                            </Listbox.Option>
                            {BUSINESS_SECTORS.map((sector) => (
                              <Listbox.Option
                                key={sector}
                                value={sector}
                                className={({ active }) =>
                                  `cursor-pointer select-none relative py-2.5 px-4 ${
                                    active ? 'bg-white/[0.08] text-white' : 'text-white/70'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <span className={selected ? 'font-semibold' : 'font-normal'}>
                                    {sector}
                                  </span>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </div>
                      </Listbox>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-semibold text-white/60">
                          Contacts
                        </label>
                        <button
                          type="button"
                          onClick={handleAddContact}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] text-white/60 border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all text-sm font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          Ajouter
                        </button>
                      </div>

                      <div className="space-y-3">
                        {formData.contacts?.map((contact, index) => (
                          <div key={index} className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.08]">
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={contact.name}
                                onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                                placeholder="Nom"
                                className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                              />
                              <input
                                type="email"
                                value={contact.email}
                                onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                                placeholder="Email"
                                className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                              />
                              <input
                                type="tel"
                                value={contact.phone}
                                onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                                placeholder="Téléphone"
                                className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                              />
                              <select
                                value={contact.type}
                                onChange={(e) => handleContactChange(index, 'type', e.target.value)}
                                className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white focus:ring-1 focus:ring-white/20 outline-none transition-all"
                                defaultValue={formData.type || 'brand'}
                              >
                                <option value="brand">Marque</option>
                                <option value="agency">Agence</option>
                              </select>
                              <div className="col-span-2">
                                <input
                                  type="text"
                                  value={contact.role || ''}
                                  onChange={(e) => handleContactChange(index, 'role', e.target.value)}
                                  placeholder="Rôle (ex: Directeur Marketing)"
                                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveContact(index)}
                              className="mt-3 p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                              title="Supprimer ce contact"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-6 border-t border-white/[0.08]">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-5 py-2.5 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2.5 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90 transition-all"
                    >
                      {editingClient ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Confirmer la suppression</h3>
              <p className="text-white/50 leading-relaxed">
                Êtes-vous sûr de vouloir supprimer le client <span className="font-semibold text-white">"{clientToDelete?.brand_name}"</span> ? Cette action est irréversible.
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-white/[0.08]">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2.5 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-500/20 border border-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-all"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}